import {
  useEffect, useMemo, useRef, useState
} from 'react';
import useSWR from 'swr';

import fetchAssays from '../../utils/api_functions/assayListRequest';
import { getTimeRange } from '../../utils/date_utils';
import { mapAssayDetails } from '../../utils/assay_utils';
import { mapAssayV2Details } from './V2Assays/conversions'
import useClientContext from '../contexts/ClientData/ClientData';

import { AssayDetailsFromV2 } from '../../types/assay.types';

export const useAssayList = ({
  pipelineID,
  pipelineName,
  workspaceID,
  assayFilter,
  validationDelay = 500,
}) => {
  const errorTimeoutRef = useRef<any>(null);

  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [errorMessage, setError] = useState<string|null>(null);

  const [currentData, setCurrentData] = useState<AssayDetailsFromV2[]>([]);
  const { isAssaysV2Enabled } = useClientContext();

  const dateObj = useMemo(() => {
    if (assayFilter?.timeRange?.duration) {
      const [timeRangeStartDate, timeRangeEndDate] = getTimeRange(assayFilter.timeRange);
      return { start: timeRangeStartDate, end: timeRangeEndDate };
    }
    return { start: assayFilter?.start, end: assayFilter?.end };
  }, [assayFilter])

  const queryArgs = [pipelineID, pipelineName, workspaceID, assayFilter, dateObj?.start ? dateObj : undefined, isAssaysV2Enabled];

  const {
    data,
    error,
    isLoading,
    mutate,
  } = useSWR(pipelineName?.length ? [pipelineName, workspaceID, 'assay-list'] : null, () => fetchAssays(queryArgs), {
    refreshInterval: 3000,
    loadingTimeout: validationDelay,
    onSuccess: () => setFirstLoad(false),
    onError: () => setFirstLoad(false),
  });

  useEffect(() => {
    if (data) {
      if (isAssaysV2Enabled) {
        setCurrentData(data?.map(mapAssayV2Details));
        setFirstLoad(false);
      } else {
        /**
         * @deprecated Assays V1 Logic
         */
        setCurrentData(data?.map(mapAssayDetails));
        setFirstLoad(false);
      }
    }
  }, [data, isAssaysV2Enabled])

  useEffect(() => {
    if (error) {
      errorTimeoutRef.current = setTimeout(() => {
        setError(error?.message || error?.msg);
      }, validationDelay);
    } else {
      clearTimeout(errorTimeoutRef.current);
      setError(null);
    }

    return () => {
      clearTimeout(errorTimeoutRef.current);
      setError(null);
    }
  }, [error, validationDelay]);

  return {
    data: currentData,
    error: errorMessage,
    isLoading,
    firstLoad,
    refetchAssays: mutate,
  };
}

export default useAssayList;
